import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const LocationContext = createContext(null);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLocations();

    // Load saved location from localStorage
    const savedLocationId = localStorage.getItem('selected_location_id');
    if (savedLocationId) {
      setSelectedLocation(parseInt(savedLocationId));
    }
  }, []);

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations?status=active');
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectLocation = (locationId) => {
    setSelectedLocation(locationId);
    if (locationId) {
      localStorage.setItem('selected_location_id', locationId.toString());
    } else {
      localStorage.removeItem('selected_location_id');
    }
  };

  const clearLocationFilter = () => {
    setSelectedLocation(null);
    localStorage.removeItem('selected_location_id');
  };

  const refreshLocations = async () => {
    await loadLocations();
  };

  const getSelectedLocationData = () => {
    if (!selectedLocation) return null;
    return locations.find(loc => loc.id === selectedLocation);
  };

  const value = {
    selectedLocation,
    locations,
    loading,
    selectLocation,
    clearLocationFilter,
    refreshLocations,
    getSelectedLocationData,
    hasLocationFilter: selectedLocation !== null
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};
